import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { attachAuthRefresh, readAuthHint, writeAuthHint, SESSION_EXPIRED_EVENT } from '../lib/authRefresh';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_TIMEOUT_MS = 15000;

// The silent 401 → /auth/refresh interceptor is installed on the default axios
// instance here AND on every axios.create() instance via lib/authRefresh.js
// (Oct 2026 fix: pages using their own instance — Enhancement schedule,
// lib/api.js, marketing, technique assessments — never refreshed, so the
// 15-minute access cookie lapsing surfaced as "Not authenticated").
attachAuthRefresh(axios);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // When a refresh fails (7-day refresh cookie gone / signed out elsewhere)
  // flip to signed-out and tell the user once; forms keep their own drafts.
  useEffect(() => {
    const onExpired = () => {
      setUser(false);
      toast.error('Your session has expired — please sign in again. Your unsaved form details have been kept.', { id: 'session-expired', duration: 8000 });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    // Per Emergent Auth playbook: if the URL still carries the OAuth
    // fragment (#session_id=...), skip the /auth/me check. AuthCallback
    // will handle the session exchange first; otherwise we race with it
    // and briefly render an unauthed state before the cookie is set.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Msg 228 — guests without an auth hint skip the /auth/me probe entirely
    // so no 401 appears in their console. If a hint is present but the
    // cookie has expired the /auth/me call will 401 once, we clear the
    // hint and the next reload is silent.
    if (!readAuthHint()) {
      setUser(false);
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  // Surface the read-only guard's 403 as a friendly toast whenever a coach
  // action is attempted during a "View as Coach" session, and collapse the
  // {code,message} detail to a plain string so no page can ever crash trying
  // to render the raw object as a React child.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const d = error?.response?.data?.detail;
        const code = d && typeof d === 'object' ? d.code : null;
        if (error?.response?.status === 403 && code === 'impersonation_read_only') {
          const msg = d.message || 'Read-only mode. Return to Admin to make changes.';
          try { error.response.data.detail = msg; } catch (_) { /* noop */ }
          try { toast.error(msg, { id: 'imp-ro' }); } catch (_) { /* noop */ }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true, timeout: AUTH_TIMEOUT_MS
      });
      setUser(data);
      writeAuthHint(true);
    } catch (error) {
      setUser(false);
      writeAuthHint(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    writeAuthHint(true);
    return data;
  };

  const register = async (email, password, name, phone) => {
    // Public registration always creates a standard customer account.
    // Role selection is not exposed at the client — the backend already
    // coerces any incoming role to `student` for the public endpoint.
    const { data } = await axios.post(
      `${API}/auth/register`,
      { email, password, name, phone },
      { withCredentials: true }
    );
    setUser(data);
    writeAuthHint(true);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    try { sessionStorage.removeItem('lgra_checkin_coach_name'); } catch (_) { /* noop */ }
    setUser(false);
    writeAuthHint(false);
  };

  // View-as-Coach (Jun 2026): clear the read-only impersonation cookie and
  // re-hydrate as the admin (whose session was never touched).
  const stopImpersonation = async () => {
    try {
      await axios.post(`${API}/impersonation/stop`, {}, { withCredentials: true });
    } catch (_) { /* clearing is best-effort */ }
    await refreshUser();
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true, timeout: AUTH_TIMEOUT_MS
      });
      setUser(data);
      writeAuthHint(true);
    } catch (error) {
      // Silent — a failed refresh doesn't warrant a console error.
      // Msg 228 A2 hardening — also flip the in-memory user state to
      // false so a mid-session expiry doesn't leave stale user data
      // in memory until the next reload.
      setUser(false);
      writeAuthHint(false);
    }
  };

  // Wrapper so external callers (AuthCallback for Google login) also
  // stamp the localStorage hint when they set the user directly.
  const setUserWithHint = (val) => {
    setUser(val);
    writeAuthHint(!!val && val !== false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      refreshUser,
      setUser: setUserWithHint,
      stopImpersonation,
      impersonation: (user && typeof user === 'object' && user._impersonation) ? user._impersonation : null,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
